import React, { useMemo } from 'react'
import { CopyTextButton } from '@clickcease/cc-client-components-lib'
import { Trans, useTranslation } from 'react-i18next'
import {
  GetTagInstructionsContainer,
  BoldBulletPoint,
  Row,
  WordpressKeysContainer,
} from './Instructions.styled'
import { StyledLink } from 'Components/Elements/Link/Link.styled'
import { BulletLine } from './elements/BulletLine'
import { getPluginUrl } from 'Consts/botMitigation'
import { InstallationKeyRow } from './elements/InstallationKeyRow/InstallationKeyRow'
import { useGetBzDetails } from 'Features/bzSetupPopup/api/getBzSecret'
import { useGetPackagingLimits } from 'Features/accountSettings/api/getPackagingLimits'
import { useSplit } from 'Hooks/useSplit'
import { BzDomain } from 'Types/TagsResponse'
import { Domain } from 'Types/Domain'

export const Wordpress = ({ domain }: { domain: Domain | undefined }) => {
  const isCopyAllEnabled = useSplit('CE_COPY_ALL_KEYS_BM')
  const { t } = useTranslation('onboarding')
  const { data: packagesData } = useGetPackagingLimits()
  const isBotZappingAllowed = packagesData?.botZappingAllowed

  const currentDomainTagId = domain?.tagId

  const domainAddress = domain?.address || ''
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [bzDetails, tags] = useGetBzDetails()

  const bzDetailsData = useMemo(() => {
    if (!(bzDetails.data instanceof Error)) {
      return bzDetails.data
    }
  }, [bzDetails])

  const authenticationKeys: Record<'domainKey' | 'authKey', string> = useMemo(() => {
    if (domainAddress && tags.data && !(tags.data instanceof Error) && tags.data.length > 0) {
      const domainFound: BzDomain | undefined = tags.data.find((domain: BzDomain) => {
        return domain.active && domain.searchId === currentDomainTagId
      })
      return { authKey: domainFound?.apiKey ?? '', domainKey: domainFound?.hash ?? '' }
    }
    return { authKey: '', domainKey: '' }
  }, [tags, domainAddress, currentDomainTagId])

  const keys = {
    domainKey: authenticationKeys.domainKey || '',
    secretKey: bzDetailsData?.secretKey || '',
    authKey: authenticationKeys.authKey || '',
  }

  const allKeys: string = JSON.stringify(keys)

  const pluginUrl = getPluginUrl(domain?.bzState, domainAddress)

  return (
    <Row id={'onboarding-wordpress-tab'}>
      <GetTagInstructionsContainer css={{ flex: 1 }}>
        <BulletLine>
          <BoldBulletPoint>&#x2022; </BoldBulletPoint>
          <Trans
            ns="onboarding"
            i18nKey="setUpTag.wordpress_step_1"
            components={{ 1: <StyledLink href={pluginUrl} target="blank" /> }}
          />
        </BulletLine>

        <BulletLine
          underBullet={
            <WordpressKeysContainer>
              <Row css={{ justifyContent: 'flex-end' }}>
                {isBotZappingAllowed && isCopyAllEnabled && (
                  <CopyTextButton
                    buttonText={t('bzSetupPopup.copy_all_keys', { ns: 'domainManagement' })}
                    value={allKeys}
                    css={{ marginRight: '$20', marginBottom: '$8' }}
                  />
                )}
              </Row>
              <InstallationKeyRow
                text={t('bzSetupPopup.domain_key', { ns: 'domainManagement' })}
                keyCode={authenticationKeys.domainKey || ''}
              />
              {isBotZappingAllowed && (
                <>
                  <InstallationKeyRow
                    text={t('bzSetupPopup.secret_key', { ns: 'domainManagement' })}
                    keyCode={bzDetailsData ? bzDetailsData.secretKey : ''}
                  />
                  <InstallationKeyRow
                    text={t('bzSetupPopup.authentication_key', { ns: 'domainManagement' })}
                    keyCode={authenticationKeys.authKey || ''}
                  />
                </>
              )}
            </WordpressKeysContainer>
          }
        >
          <BoldBulletPoint>&#x2022; </BoldBulletPoint>{' '}
          {t('setUpTag.wordpressStep2', { count: isBotZappingAllowed ? 3 : 1 })}
        </BulletLine>
      </GetTagInstructionsContainer>
    </Row>
  )
}
